import { NetworkStatus, Reference, useMutation } from '@apollo/client';
import { Partner, PreSoldMembership } from '@provision/entity-types';
import { getFullName } from '@provision/formatting-utils';
import { gql, QueryHookOptions, useQuery } from '@provision/graphql-client';
import { useCallback, useMemo } from 'react';
import { isNullish } from 'remeda';
import { CppPayload } from 'src/components/AddComplimentaryPeriodModal/add-complimentary-period-modal';
import { PsmPayload } from 'src/components/AddPreSoldMembershipModal/add-pre-sold-membership-modal';
import { Address, mapAddress } from 'src/graphql/entities/address';
import {
  ADDRESS_FIELDS,
  ADDRESS_RELATIONS,
} from 'src/graphql/entities/address/address.fragments';
import {
  SubscriptionPhase,
  SubscriptionStatus,
} from 'src/graphql/entities/address-subscription/types';
import {
  buildDynamicAttributeVariables,
  ProjectedDynamicAttribute,
} from 'src/graphql/entities/custom-attributes/dynamic-attributes-projection';
import { META_DATA_SEARCH_QUERY_FIELDS } from 'src/graphql/entities/metadata.fragments';
import { PARTNER_FIELDS, PARTNER_RELATIONS } from 'src/graphql/entities/partner';
import { PLATFORM_FIELDS } from 'src/graphql/entities/platform-user';
import { PRE_SOLD_MEMBERSHIP_FIELDS } from 'src/graphql/entities/pre-sold-membership/pre-sold-membership.fragments';
import { ProjectStages } from 'src/graphql/entities/project-stage';
import { formatServiceLevel } from 'src/graphql/entities/stripe-subscription-schedule';
import {
  ConditionInput,
  QueryVariables,
  Records,
  SearchInput,
  SortingInput,
} from 'src/graphql/types';
import { generateFieldModifiersMap, useLoadMore } from 'src/graphql/utils';
import { ProjectRaw } from 'src/types';
import { useNotifications } from 'src/utils/hooks';
import { useCacheManager } from 'src/utils/hooks/use-cache-manager';
import { removeNotDefinedProperties } from 'src/utils/objects';
import { parseProjectActionError } from './parse-project-action-error';
import { PROJECT_FIELDS } from './project.fragments';

const GET_PROJECTS_QUERY = gql`
  query GetProject(
    $params: QueryParamsWithSearch
    $skipTotalRecords: Boolean = false
    $dynamicAttributePaths: [String!]
    $hasCustomAttributes: Boolean = false
  ) @entity(name: "Project") {
    getProject(queryParams: $params) {
      records {
        ...ProjectFields
        platformUser {
          ...PlatformUserFields
        }
        projectStage {
          id
          name
        }
        address {
          ...AddressFields
          ...AddressRelations
        }
        partner {
          ...PartnerFields
          ...PartnerRelations
        }
        dynamicAttributes(attributes: $dynamicAttributePaths)
          @include(if: $hasCustomAttributes) {
          attribute
          value
        }
      }
      metadata {
        ...MetaDataSearchQueryFields
      }
    }
  }
  ${PROJECT_FIELDS}
  ${PLATFORM_FIELDS}
  ${ADDRESS_FIELDS}
  ${ADDRESS_RELATIONS}
  ${PARTNER_FIELDS}
  ${PARTNER_RELATIONS}
  ${META_DATA_SEARCH_QUERY_FIELDS}
`;

const UPDATE_PROJECT_MUTATION = gql`
  mutation UpdateProject($conditions: [ConditionInput], $data: ProjectDataInput) {
    updateProject(conditions: $conditions, data: $data) {
      ...ProjectFields
    }
  }
  ${PROJECT_FIELDS}
`;

const CREATE_PROJECT_WITH_SIDE_EFFECTS_MUTATION = gql`
  mutation CreateProjectWithSideEffects($params: CreateProjectWithSideEffectsInput!) {
    createProjectWithSideEffects(params: $params) {
      success
      project {
        ...ProjectFields
      }
      address {
        ...AddressFields
      }
    }
  }
  ${PROJECT_FIELDS}
  ${ADDRESS_FIELDS}
`;

const DELETE_PROJECT_MUTATION = gql`
  mutation DeleteProject($conditions: [ConditionInput]) {
    deleteProject(conditions: $conditions) {
      id
    }
  }
`;

const SUBMIT_DAY_1_MUTATION = gql`
  mutation SubmitDay1($params: SubmitDay1Input!) {
    submitDay1(params: $params) {
      address {
        ...AddressFields
      }
      preSoldMembership {
        ...PreSoldMembershipFields
      }
      project {
        ...ProjectFields
      }
      subscriptionId
    }
  }
  ${ADDRESS_FIELDS}
  ${PRE_SOLD_MEMBERSHIP_FIELDS}
  ${PROJECT_FIELDS}
`;

const SUBMIT_PROPOSAL_WON_MUTATION = gql`
  mutation SubmitProposalWon($params: SubmitProposalWonInput!) {
    submitProposalWon(params: $params) {
      address {
        ...AddressFields
      }
      project {
        ...ProjectFields
      }
    }
  }
  ${ADDRESS_FIELDS}
  ${PROJECT_FIELDS}
`;

const SUBMIT_PROPOSAL_LOST_MUTATION = gql`
  mutation SubmitProposalLost($params: SubmitProposalLostInput!) {
    submitProposalLost(params: $params) {
      project {
        ...ProjectFields
      }
    }
  }
  ${PROJECT_FIELDS}
`;

export type GetProjectsQuery = {
  getProject: Records<ProjectRaw & { dynamicAttributes?: ProjectedDynamicAttribute[] }>;
};

export type Project = Pick<
  ProjectRaw,
  | 'id'
  | 'addressId'
  | 'projectId'
  | 'name'
  | 'projectStageId'
  | 'projectStage'
  | 'ownerId'
  | 'lostReasonNotes'
  | 'isDay1DateFinalized'
  | 'createdAt'
  | 'updatedAt'
> & {
  projectManagerFullName: string;
  partnerId?: number;
  warrantyExpirationDate?: string | null;
  estimatedDay1Date?: string | null;
  day1StartDate?: string | null;
  signingDate?: string | null;
  warrantyLength?: number | null;
  price?: string | null;
  projectLostReasonId?: number | null;
  isPrimary?: boolean | null;
  isPrimaryForAddress?: boolean | null;
  customAttributes?: string | null;
  address?: Partial<Address>;
  partner?: Partner;
};

type CreateProjectWithSideEffects = {
  createProjectWithSideEffects: {
    success: boolean;
    address: Partial<Address> & { __typename: string };
    project: Partial<Project> & { partnerId: number; __typename: string };
  };
};

type SubmitDay1 = {
  submitDay1: {
    address: Partial<Address> & { __typename: string };
    project: Partial<Project> & { __typename: string };
    preSoldMembership?: Partial<PreSoldMembership> & { __typename: string };
    subscriptionId?: string | null;
  };
};

type SubmitProposalWon = {
  submitProposalWon: {
    address: Partial<Address> & { __typename: string };
    project: Partial<Project> & { __typename: string };
  };
};

type SubmitProposalLost = {
  submitProposalLost: {
    project: Partial<Project> & { __typename: string };
    address: Partial<Address> & { __typename: string };
  };
};

export interface SubmitDay1Payload {
  partnerId: number;
  projectId: string;
  addressId: string;
  day1Date: string;
  membershipSalesNotes?: string;
  cpp?: CppPayload;
  psm?: PsmPayload;
}

export interface SubmitProposalWonPayload {
  partnerId: number;
  projectId: string;
  addressId: string;
  desiredMembership: string;
  estimatedDay1: string;
  warrantyLength: number;
  tosAccepted: boolean;
  sendEducationalEmails: boolean;
  orgNotes?: string;
  membershipSalesNotes?: string;
}

export interface SubmitProposalLostPayload {
  partnerId: number;
  projectId: string;
  addressId: string;
  projectLostReasonId: number;
  lostReasonNotes?: string | null;
}

export interface CreateProjectPayload {
  partnerId: number;
  addressId: string;
  name: string;
  projectStageId: number;
  warrantyLength?: number | null;
  warrantyExpiration?: string | null;
  projectManagerId?: string;
  signingDate?: string | null;
  estimatedDay1?: string | null;
  day1Date?: string | null;
  tosAccepted: string | null;
  willReceiveEmails: boolean;
}

export const useProjects = (
  params: {
    partnerId?: number | null;
    filters?: ConditionInput[];
    limit?: number;
    sort?: SortingInput[] | SortingInput;
    searchParams?: SearchInput;
    dynamicAttributePaths?: string[];
  },
  options?: QueryHookOptions<GetProjectsQuery>,
) => {
  const queryParams = useMemo(() => {
    const filters = [...(params.filters ?? [])];

    if (!isNullish(params.partnerId)) {
      filters.push({
        attribute: 'partnerId',
        operator: '=',
        value: String(params.partnerId),
      });
    }

    return {
      filters,
      searchParams: params.searchParams,
      sorting: params.sort,
      limit: params.limit,
    };
  }, [params.sort, params.limit, params.partnerId, params.filters, params.searchParams]);

  const useQueryResult = useQuery<GetProjectsQuery, QueryVariables>(GET_PROJECTS_QUERY, {
    ...options,
    skip: options?.skip,
    notifyOnNetworkStatusChange: true,
    variables: {
      skipTotalRecords: !!queryParams.searchParams,
      ...buildDynamicAttributeVariables(params.dynamicAttributePaths),
      params: queryParams,
    },
  });

  const { data, fetchMore, variables, networkStatus, ...rest } = useQueryResult;
  const cacheManager = useCacheManager<GetProjectsQuery, QueryVariables>(
    GET_PROJECTS_QUERY,
    useQueryResult,
  );

  const projects = useMemo(
    () => mapProjects(data?.getProject.records || []),
    [data?.getProject.records],
  );

  const { loadMore, hasReachedEnd } = useLoadMore<GetProjectsQuery>({
    offset: projects?.length ?? 0,
    fetchMore,
    params: variables?.params!,
  });

  const totalRecords = data?.getProject?.metadata?.totalRecords ?? 0;

  return {
    projects,
    totalRecords,
    loadMore,
    isLoadingMore: networkStatus === NetworkStatus.fetchMore,
    hasMore: data?.getProject.metadata?.totalRecords
      ? totalRecords > (data?.getProject?.records?.length ?? 0)
      : !hasReachedEnd,
    cacheManager,
    ...rest,
  };
};

export const useProject = (projectId?: string) => {
  const { showErrorAlert } = useNotifications();

  const [updateProjectMutation] = useMutation<{
    updateProject: Partial<Project & { __typename: string }>[];
  }>(UPDATE_PROJECT_MUTATION, {
    onError: (error) => {
      console.error(error);
      showErrorAlert(parseProjectActionError(error));
    },
  });

  const updateProject = useCallback(
    (project: Partial<Project>) => {
      return updateProjectMutation({
        variables: {
          conditions: [{ attribute: 'projectId', operator: '=', value: String(projectId) }],
          data: {
            ...project,
            projectStage: undefined,
          },
        },
        optimisticResponse: {
          updateProject: [
            {
              __typename: 'Project',
              id: projectId,
              projectId,
              addressId: project.addressId || 'temp-address-id',
              partnerId: project.partnerId || 0,
              createdAt: project.createdAt || new Date().toISOString(),
              updatedAt: new Date().toISOString(),
              name: project.name,
              isPrimary: project.isPrimary || false,
              projectStageId: project.projectStageId,
              projectStage: project.projectStage,
              ownerId: project.ownerId || 'temp-owner-id',
              estimatedDay1Date: project.estimatedDay1Date,
              day1StartDate: project.day1StartDate,
              warrantyExpirationDate: project.warrantyExpirationDate,
              signingDate: project.signingDate,
              projectLostReasonId: project.projectLostReasonId,
              lostReasonNotes: project.lostReasonNotes,
            },
          ],
        },
        update: (cache, { data }) => {
          if (!data?.updateProject?.length) return;

          const updatedProject = removeNotDefinedProperties(data.updateProject[0]);
          const id = cache.identify({
            __typename: 'Project',
            id: updatedProject.projectId,
          });

          const fields = generateFieldModifiersMap({
            ...updatedProject,
          });

          cache.modify({
            id,
            fields: {
              ...fields,
              projectStage(existingItem = {}) {
                const existingProjectStage = existingItem as Reference;
                if (project.projectStage) {
                  return project.projectStage;
                }

                return existingProjectStage;
              },
              platformUser(existingItem = {}, { toReference }) {
                const existingUser = existingItem as Reference;
                if (!project.ownerId) {
                  return existingUser;
                }

                return toReference({
                  id: project.ownerId,
                  __typename: 'PlatformUser',
                });
              },
            },
          });
        },
      });
    },
    [projectId, updateProjectMutation],
  );

  return {
    updateProject,
  };
};

export const useCreateProject = (options?: {
  filters?: ConditionInput[];
  limit?: number;
  sort?: SortingInput[] | SortingInput;
  offset?: number;
}) => {
  const { filters, limit, sort, offset = 0 } = options ?? {};
  const { showErrorAlert } = useNotifications();

  const [createProjectMutation] = useMutation<CreateProjectWithSideEffects>(
    CREATE_PROJECT_WITH_SIDE_EFFECTS_MUTATION,
    {
      onError: (error) => {
        console.error(error);
        showErrorAlert(parseProjectActionError(error));
      },
    },
  );

  const createProject = useCallback(
    (data: CreateProjectPayload, address?: Address) => {
      const payload = {
        partnerId: data.partnerId,
        addressId: data.addressId,
        name: data.name,
        projectStageId: data.projectStageId,
        warrantyLength: data.warrantyLength,
        warrantyExpiration: data.warrantyExpiration,
        ownerId: data.projectManagerId,
        signingDate: data.signingDate,
        estimatedDay1: data.estimatedDay1,
        day1Date: data.day1Date,
        tosAccepted: data.tosAccepted,
        willReceiveEmails: data.willReceiveEmails,
      };

      return createProjectMutation({
        variables: {
          params: payload,
        },
        optimisticResponse: {
          createProjectWithSideEffects: {
            success: true,
            project: {
              __typename: 'Project',
              id: 'temp-project-id',
              projectId: 'temp-project-id',
              addressId: payload.addressId,
              name: payload.name,
              projectStageId: payload.projectStageId,
              ownerId: payload.ownerId || 'temp-owner-id',
              signingDate: payload.signingDate,
              estimatedDay1Date: payload.estimatedDay1,
              day1StartDate: payload.day1Date,
              warrantyExpirationDate: payload.warrantyExpiration,
              isPrimary: false,
              projectLostReasonId: 1,
              lostReasonNotes: '',
              partnerId: payload.partnerId,
              createdAt: new Date().toISOString(),
              updatedAt: new Date().toISOString(),
            },
            address: {
              ...(address || {}),
              __typename: 'Address',
              id: payload.addressId,
              addressId: payload.addressId,
              tosAccepted: payload.tosAccepted || undefined,
            },
          },
        },
        update: (cache, { data }) => {
          if (!data?.createProjectWithSideEffects?.project) return;

          const createdProject = data.createProjectWithSideEffects.project;
          const updatedAddress = data.createProjectWithSideEffects.address;
          const id = cache.identify({
            __typename: 'Address',
            id: data.createProjectWithSideEffects.project.addressId,
          });

          cache.updateQuery<GetProjectsQuery, QueryVariables>(
            {
              query: GET_PROJECTS_QUERY,
              variables: {
                skipTotalRecords: false,
                params: {
                  filters,
                  limit,
                  offset,
                  sorting: sort ?? [{ attribute: 'updatedAt', desc: true }],
                },
              },
            },
            (existingData) => {
              if (!existingData?.getProject?.records) return;

              const records = [...existingData.getProject.records];

              if (createdProject) {
                // We know createdProject is a Partial<Project>, so it should be safe to
                // cast it as ProjectRaw
                records.push(createdProject as unknown as ProjectRaw);
              }

              return {
                getProject: {
                  __typename: 'ProjectDataResponse',
                  records,
                  metadata: existingData.getProject.metadata ?? {},
                },
              };
            },
          );

          cache.modify({
            id,
            fields: {
              projects(existingItems = []) {
                const existingProjects = existingItems as Reference[];
                return [...existingProjects, createdProject];
              },
              ...(updatedAddress ? { tosAccepted: () => updatedAddress.tosAccepted } : {}),
            },
          });
        },
      });
    },
    [createProjectMutation, filters, limit, offset, sort],
  );

  return {
    createProject,
  };
};

export const useSubmitDay1 = () => {
  const { showErrorAlert } = useNotifications();

  const [submitDay1Mutation] = useMutation<SubmitDay1>(SUBMIT_DAY_1_MUTATION, {
    onError: (error) => {
      console.log(error);
      showErrorAlert(parseProjectActionError(error));
    },
  });

  const submitDay1 = useCallback(
    (payload: SubmitDay1Payload, project: Project, address: Address) => {
      return submitDay1Mutation({
        variables: {
          params: payload,
        },
        optimisticResponse: {
          submitDay1: {
            address: {
              ...address,
              __typename: 'Address',
              id: payload.addressId,
              addressId: payload.addressId,
              membershipSalesNotes: payload.membershipSalesNotes,
              cppExpDate: payload.cpp?.expDate,
            },
            preSoldMembership: payload.psm
              ? {
                  __typename: 'PreSoldMembership',
                  id: 'temp-pre-sold-id',
                  preSoldMembershipId: 'temp-pre-sold-id',
                  addressId: payload.addressId,
                  partnerId: payload.partnerId,
                  endDate: new Date().toISOString(),
                  isScheduled: true,
                }
              : undefined,
            project: {
              ...project,
              __typename: 'Project',
              id: payload.projectId,
              projectId: payload.projectId,
              addressId: project.addressId || 'temp-address-id',
              name: project.name,
              projectStageId: ProjectStages.ServiceClient,
              ownerId: project.ownerId,
              signingDate: null,
              estimatedDay1Date: payload.day1Date,
              day1StartDate: payload.day1Date,
              warrantyExpirationDate: null,
              warrantyLength: null,
              price: null,
              projectLostReasonId: null,
              isPrimary: null,
              customAttributes: null,
              isDay1DateFinalized: true,
              lostReasonNotes: '',
              createdAt: new Date().toISOString(),
              updatedAt: new Date().toISOString(),
              partnerId: payload.partnerId,
            },
          },
        },
        update: (cache, { data }) => {
          if (!data?.submitDay1?.project) return;

          const updatedProject = data.submitDay1.project;
          const updatedAddress = data.submitDay1.address;
          const addedPreSoldMembership = data.submitDay1.preSoldMembership;
          const cacheProjectId = cache.identify({
            __typename: 'Project',
            id: updatedProject?.projectId,
          });
          const cacheAddressId = cache.identify({
            __typename: 'Address',
            id: updatedAddress?.addressId,
          });

          let subscriptionPhase = SubscriptionPhase.Active;
          let subscriptionStatus = SubscriptionStatus.Active;

          if (payload.cpp?.expDate) {
            subscriptionPhase = SubscriptionPhase.Trial;
            subscriptionStatus = SubscriptionStatus.Trialing;
          } else if (payload.psm?.partnerProductId) {
            subscriptionPhase = SubscriptionPhase.Psm;
            subscriptionStatus = SubscriptionStatus.Trialing;
          }

          cache.modify({
            id: cacheAddressId,
            fields: generateFieldModifiersMap({
              ...removeNotDefinedProperties({
                ...updatedAddress,
                preSoldMemberships: addedPreSoldMembership ? [addedPreSoldMembership] : [],
              }),
              addressSubscriptions: [
                {
                  __typename: 'AddressSubscription',
                  id: 'temp-day1-sub-id',
                  addressSubscriptionId: 'temp-day1-sub-id',
                  stripeSubscriptionId: data.submitDay1.subscriptionId || 'temp-day1-sub-id',
                  partnerId: payload.partnerId,
                  addressId: payload.addressId,
                  phase: subscriptionPhase,
                  status: subscriptionStatus,
                  createdAt: new Date().toISOString(),
                  updatedAt: new Date().toISOString(),
                },
              ],
            }),
          });

          cache.modify({
            id: cacheProjectId,
            fields: {
              ...generateFieldModifiersMap({
                ...updatedProject,
              }),
              projectStage(existingItem = {}) {
                const existingProjectStage = existingItem as Reference;
                if (project.projectStage) {
                  return project.projectStage;
                }

                return existingProjectStage;
              },
              platformUser(existingItem = {}, { toReference }) {
                const existingUser = existingItem as Reference;
                if (!project.ownerId) {
                  return existingUser;
                }

                return toReference({
                  id: project.ownerId,
                  __typename: 'PlatformUser',
                });
              },
            },
          });

          cache.evict({
            id: 'ROOT_QUERY',
            fieldName: 'getStripeSubscriptionDetails',
          });
        },
      });
    },
    [submitDay1Mutation],
  );

  return {
    submitDay1,
  };
};

export const useSubmitProposalWon = () => {
  const { showErrorAlert } = useNotifications();

  const [submitProposalWonMutation] = useMutation<SubmitProposalWon>(
    SUBMIT_PROPOSAL_WON_MUTATION,
    {
      onError: (error) => {
        console.error(error);
        showErrorAlert(parseProjectActionError(error));
      },
    },
  );

  const submitProposalWon = useCallback(
    (payload: SubmitProposalWonPayload, project: Project, address: Address) => {
      return submitProposalWonMutation({
        variables: {
          params: payload,
        },
        optimisticResponse: {
          submitProposalWon: {
            address: {
              ...address,
              __typename: 'Address',
              id: payload.addressId,
              addressId: payload.addressId,
              membershipSalesNotes: payload.membershipSalesNotes,
              orgNotes: payload.orgNotes,
              clientSelectedMembership: payload?.desiredMembership
                ? formatServiceLevel(payload?.desiredMembership)
                : undefined,
              tosAccepted: payload.tosAccepted ? 'Yes' : 'No',
            },
            project: {
              ...project,
              __typename: 'Project',
              id: payload.projectId,
              projectId: payload.projectId,
              addressId: project.addressId || 'temp-address-id',
              name: project.name,
              projectStageId: ProjectStages.ActiveProject,
              ownerId: project.ownerId,
              signingDate: new Date().toISOString(),
              estimatedDay1Date: payload.estimatedDay1,
              day1StartDate: null,
              warrantyExpirationDate: null,
              warrantyLength: null,
              price: null,
              projectLostReasonId: null,
              isPrimary: null,
              customAttributes: null,
              isDay1DateFinalized: true,
              lostReasonNotes: '',
              createdAt: new Date().toISOString(),
              updatedAt: new Date().toISOString(),
              partnerId: payload.partnerId,
            },
          },
        },
        update: (cache, { data }) => {
          if (!data?.submitProposalWon?.project) return;

          const updatedProject = data.submitProposalWon.project;
          const updatedAddress = data.submitProposalWon.address;
          const cacheProjectId = cache.identify({
            __typename: 'Project',
            id: updatedProject?.projectId,
          });
          const cacheAddressId = cache.identify({
            __typename: 'Address',
            id: updatedAddress?.addressId,
          });

          cache.modify({
            id: cacheAddressId,
            fields: generateFieldModifiersMap({
              ...removeNotDefinedProperties({
                ...updatedAddress,
              }),
            }),
          });

          cache.modify({
            id: cacheProjectId,
            fields: {
              ...generateFieldModifiersMap({
                ...updatedProject,
              }),
              projectStage(existingItem = {}) {
                const existingProjectStage = existingItem as Reference;
                if (project.projectStage) {
                  return project.projectStage;
                }

                return existingProjectStage;
              },
              platformUser(existingItem = {}, { toReference }) {
                const existingUser = existingItem as Reference;
                if (!project.ownerId) {
                  return existingUser;
                }

                return toReference({
                  id: project.ownerId,
                  __typename: 'PlatformUser',
                });
              },
            },
          });
        },
      });
    },
    [submitProposalWonMutation],
  );

  return {
    submitProposalWon,
  };
};

export const useSubmitProposalLost = () => {
  const { showErrorAlert } = useNotifications();

  const [submitProposalLostMutation] = useMutation<SubmitProposalLost>(
    SUBMIT_PROPOSAL_LOST_MUTATION,
    {
      onError: (error) => {
        console.error(error);
        showErrorAlert(parseProjectActionError(error));
      },
    },
  );

  const submitProposalLost = useCallback(
    (payload: SubmitProposalLostPayload, project: Project, address: Address) => {
      return submitProposalLostMutation({
        variables: {
          params: payload,
        },
        optimisticResponse: {
          submitProposalLost: {
            address: {
              ...address,
              __typename: 'Address',
              id: address.addressId,
              addressId: address.addressId,
              primaryProjectId: undefined,
            },
            project: {
              ...project,
              __typename: 'Project',
              id: payload.projectId,
              projectId: payload.projectId,
              addressId: project.addressId || 'temp-address-id',
              name: project.name,
              projectStageId: ProjectStages.ProposalLost,
              ownerId: project.ownerId,
              signingDate: new Date().toISOString(),
              estimatedDay1Date: null,
              day1StartDate: null,
              warrantyExpirationDate: null,
              warrantyLength: null,
              price: null,
              projectLostReasonId: payload.projectLostReasonId,
              isPrimary: false,
              customAttributes: null,
              isDay1DateFinalized: true,
              lostReasonNotes: payload.lostReasonNotes || '',
              createdAt: new Date().toISOString(),
              updatedAt: new Date().toISOString(),
              partnerId: payload.partnerId,
            },
          },
        },

        update: (cache, { data }) => {
          if (!data?.submitProposalLost?.project) return;

          const updatedProject = data.submitProposalLost.project;
          const updatedAddress = data.submitProposalLost.address;
          const cacheProjectId = cache.identify({
            __typename: 'Project',
            id: updatedProject?.projectId,
          });
          const cacheAddressId = cache.identify({
            __typename: 'Address',
            id: updatedAddress?.addressId,
          });

          cache.modify({
            id: cacheAddressId,
            fields: generateFieldModifiersMap({
              ...removeNotDefinedProperties({
                ...updatedAddress,
              }),
            }),
          });

          cache.modify({
            id: cacheProjectId,
            fields: {
              ...generateFieldModifiersMap({
                ...updatedProject,
              }),
              projectStage(existingItem = {}) {
                const existingProjectStage = existingItem as Reference;
                if (project.projectStage) {
                  return project.projectStage;
                }

                return existingProjectStage;
              },
              platformUser(existingItem = {}, { toReference }) {
                const existingUser = existingItem as Reference;
                if (!project.ownerId) {
                  return existingUser;
                }

                return toReference({
                  id: project.ownerId,
                  __typename: 'PlatformUser',
                });
              },
            },
          });
        },
      });
    },
    [submitProposalLostMutation],
  );

  return {
    submitProposalLost,
  };
};

export const useProjectRecord = ({ projectId }: { projectId: string }) => {
  const { projects } =
    useProjects(
      {
        filters: [
          {
            attribute: 'projectId',
            operator: '=',
            value: projectId,
          },
        ],
      },
      {
        skip: !projectId,
      },
    ) ?? {};

  const selectedProject = useMemo(
    () => (projectId ? projects?.find((p) => p.projectId === projectId) : undefined),
    [projectId, projects],
  );

  return selectedProject;
};

function mapProjects(projects: ProjectRaw[]): Project[] {
  return projects.map(mapProject);
}

export function mapProject(project: ProjectRaw): Project {
  const address = project.address ? mapAddress(project.address) : undefined;

  return {
    ...project,
    address,
    isPrimaryForAddress:
      !!address?.primaryProjectId && address?.primaryProjectId === project.id,
    projectManagerFullName: getFullName(project.platformUser),
  };
}

export const useDeleteProject = () => {
  const { showErrorAlert } = useNotifications();

  const [deleteProjectMutation] = useMutation<{
    deleteProject: Partial<Project>[];
  }>(DELETE_PROJECT_MUTATION, {
    onError: (error) => {
      console.error(error);
      showErrorAlert(parseProjectActionError(error));
    },
  });

  const deleteProject = useCallback(
    (id: string) => {
      return deleteProjectMutation({
        variables: {
          conditions: [{ attribute: 'projectId', operator: '=', value: id }],
        },
        optimisticResponse: { deleteProject: [{ id }] },
        update: (cache, { data }) => {
          if (!data?.deleteProject?.length) return;
          const deletedProject = data.deleteProject[0];
          const id = cache.identify({
            __typename: 'Project',
            id: deletedProject.id,
          });

          cache.evict({ id });
        },
      });
    },
    [deleteProjectMutation],
  );

  return {
    deleteProject,
  };
};
